/**
 * AJAX facet filtering for collection and search pages.
 * Fetches the current section through the Section Rendering API and swaps the
 * results in place
 */

class FacetFiltersForm extends HTMLElement {
  static debounceDelay = 400;
  static cacheLimit = 20;
  static countIds = ['ProductCount', 'ProductCountDesktop', 'ProductCountMobile', 'ProductCountSearch'];

  static cache = new Map();
  static controller = null;
  static searchParamsInitial = window.location.search.slice(1);
  static searchParamsPrev = window.location.search.slice(1);

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const form = this.querySelector('form');
    if (!form) return;

    this.debouncedSubmit = debounce((event) => this.onSubmitHandler(event), FacetFiltersForm.debounceDelay);

    if (form.id === 'FacetFiltersFormMobile') {
      // In the mobile drawer nothing is applied until the shopper taps "Toepassen",
      // so the pending selection has to be reflected locally.
      form.addEventListener('change', (event) => FacetFiltersForm.markPendingSelection(form, event.target));

      form.addEventListener('click', (event) => {
        const applyButton = event.target.closest('[data-facet-apply]');
        if (!applyButton) return;
        this.onSubmitHandler(event);
        FacetFiltersForm.closeMobileDrawer(applyButton);
      });
    } else {
      // Typed values are debounced; checkboxes, selects and range handles apply on change.
      form.addEventListener('input', (event) => {
        if (FacetFiltersForm.isTypedInput(event.target)) this.debouncedSubmit(event);
      });
      form.addEventListener('change', (event) => {
        if (!FacetFiltersForm.isTypedInput(event.target)) this.onSubmitHandler(event);
      });
    }

    form.addEventListener('submit', (event) => this.onSubmitHandler(event));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static isTypedInput(element) {
    return element?.matches?.('input[type="text"], input[type="number"], input[type="search"]');
  }

  static closeMobileDrawer(element) {
    element.closest('.mobile-facets__wrapper')?.querySelector('summary')?.click();
  }

  /** Mirrors what the server render would show: highlighted rows and a per-filter count. */
  static markPendingSelection(form, changed) {
    if (changed?.type === 'checkbox') {
      changed.closest('.facets__label, .mobile-facets__label')?.classList.toggle('active', changed.checked);
    }

    form.querySelectorAll('.mobile-facets__details').forEach((details) => {
      const badge = details.querySelector('.mobile-facets__selected');
      if (!badge) return;

      const count = details.querySelectorAll('input[type="checkbox"]:checked').length;
      badge.textContent = `(${count})`;
      badge.classList.toggle('hidden', count === 0);
    });
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state?.searchParams ?? FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static getSectionId() {
    return (
      document.getElementById('product-grid')?.dataset.id ||
      document.querySelector('.facets-wrapper[data-id]')?.dataset.id
    );
  }

  /** Merges every relevant facet form into one query string. */
  static createSearchParams(forms) {
    const params = new URLSearchParams();

    forms.forEach((form) => {
      for (const [key, value] of new FormData(form)) {
        if (value === '') continue;
        // Single-value params must not stack up when several forms carry them.
        if (key === 'sort_by' || key === 'q' || key === 'options[prefix]') params.set(key, value);
        else params.append(key, value);
      }
    });

    // Drop bounds that still sit at the full range, so an untouched price filter stays out of the URL.
    forms.forEach((form) => {
      form.querySelectorAll('[data-default-value]').forEach((input) => {
        if (input.name && input.value === input.dataset.defaultValue) params.delete(input.name);
      });
    });

    return params.toString();
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const sectionId = FacetFiltersForm.getSectionId();
    if (!sectionId) return;

    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.setLoading(true);

    // A newer request always wins from the one still in flight.
    FacetFiltersForm.controller?.abort();

    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
    let html = FacetFiltersForm.cache.get(url);

    if (html === undefined) {
      const controller = new AbortController();
      FacetFiltersForm.controller = controller;

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        html = await response.text();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Fall back to a server-rendered page rather than leaving stale results.
        window.location.search = searchParams;
        return;
      }

      FacetFiltersForm.cache.set(url, html);
      if (FacetFiltersForm.cache.size > FacetFiltersForm.cacheLimit) {
        FacetFiltersForm.cache.delete(FacetFiltersForm.cache.keys().next().value);
      }
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    FacetFiltersForm.render(html, event);
  }

  static render(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const update = () => {
      FacetFiltersForm.renderProductGridContainer(parsed);
      FacetFiltersForm.renderProductCount(parsed);
      FacetFiltersForm.renderFilters(parsed, event);
      FacetFiltersForm.setLoading(false);
      document.dispatchEvent(new CustomEvent('facets:updated', { detail: { html: parsed } }));
    };

    // Matches the media query that names #ProductGridContainer; on mobile the drawer
    // must stay above the results, so there the swap happens without a transition.
    const canTransition =
      typeof document.startViewTransition === 'function' &&
      window.matchMedia('(min-width: 750px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (canTransition) document.startViewTransition(update);
    else update();
  }

  static setLoading(isLoading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', String(isLoading));
    grid?.querySelector('.collection')?.classList.toggle('loading', isLoading);

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));

    FacetFiltersForm.countIds.forEach((id) =>
      document.getElementById(id)?.classList.toggle('loading', isLoading)
    );
  }

  static renderProductGridContainer(parsed) {
    const source = parsed.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;

    target.innerHTML = source.innerHTML;
    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(parsed) {
    // An empty result set drops the in-grid count, so fall back to the sidebar one.
    const source = FacetFiltersForm.countIds.map((id) => parsed.getElementById(id)).find(Boolean);
    if (!source) return;

    FacetFiltersForm.countIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) element.innerHTML = source.innerHTML;
    });
  }

  static renderFilters(parsed, event) {
    const selector = '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter';
    const facetsFromFetch = Array.from(parsed.querySelectorAll(selector));
    const facetsFromDom = Array.from(document.querySelectorAll(selector));

    // Drop filters that no longer apply to the current result set.
    facetsFromDom.forEach((element) => {
      if (!facetsFromFetch.some(({ id }) => element.id === id)) element.remove();
    });

    const activeFilter = event?.target.closest?.('.js-filter');
    const matchesActive = (element) => (activeFilter ? element.id === activeFilter.id : false);

    const facetsToRender = facetsFromFetch.filter((element) => !matchesActive(element));
    const facetToCount = facetsFromFetch.find(matchesActive);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      // Replacing the inner HTML keeps the <details> open state intact.
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
        return;
      }

      const previous = facetsToRender[index - 1];
      if (previous && previous.className === elementToRender.className) {
        document.getElementById(previous.id)?.after(elementToRender);
        return;
      }

      const parentId = elementToRender.parentElement?.id;
      if (parentId) document.querySelector(`#${parentId} .js-filter`)?.before(elementToRender);
    });

    FacetFiltersForm.renderActiveFacets(parsed);
    FacetFiltersForm.renderAdditionalElements(parsed);

    // The filter the shopper is using only gets its counts refreshed, so focus is kept.
    const target = facetToCount ? document.getElementById(activeFilter.id) : null;
    if (target) {
      FacetFiltersForm.renderCounts(facetToCount, target);
      FacetFiltersForm.renderMobileCounts(facetToCount, target);

      const isMobile = target.classList.contains('mobile-facets__details');
      const toActivate = target.querySelector(isMobile ? '.mobile-facets__close-button' : '.facets__summary');
      const keepFocus = FacetFiltersForm.isTypedInput(event.target) || event.target.closest?.('[data-facet-apply]');
      if (toActivate && !keepFocus) toActivate.focus();
    }

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) FacetFiltersForm.markPendingSelection(mobileForm, null);

    window.initPriceRangeSliders?.();
  }

  static renderActiveFacets(parsed) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const source = parsed.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) {
        target.innerHTML = source.innerHTML;
        target.classList.toggle('hidden', source.classList.contains('hidden'));
      }
    });
  }

  static renderAdditionalElements(parsed) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = parsed.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeader = target.querySelector('.facets__header');
    const sourceHeader = source.querySelector('.facets__header');
    if (sourceHeader && targetHeader) targetHeader.outerHTML = sourceHeader.outerHTML;

    const targetWrap = target.querySelector('.facets-wrap');
    const sourceWrap = source.querySelector('.facets-wrap');
    if (sourceWrap && targetWrap) {
      // Keep an expanded "show more" list expanded.
      if (target.querySelector('show-more-button .label-show-more.hidden')) {
        sourceWrap
          .querySelectorAll('.facets__item.hidden')
          .forEach((item) => item.classList.replace('hidden', 'show-more-item'));
      }
      targetWrap.outerHTML = sourceWrap.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const sourceList = source?.querySelector('.mobile-facets__list');
    const targetList = target?.querySelector('.mobile-facets__list');
    if (sourceList && targetList) targetList.outerHTML = sourceList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && `?${searchParams}`}`);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // A debounced submit can fire after its input was swapped out, so fall back to this element's form.
    const fromEvent = event.target.closest?.('form');
    const origin = fromEvent?.isConnected ? fromEvent : this.querySelector('form');
    const isMobile = origin?.id === 'FacetFiltersFormMobile';
    const forms = Array.from(document.querySelectorAll('facet-filters-form form')).filter((form) =>
      isMobile ? form.id === 'FacetFiltersFormMobile' : form.id !== 'FacetFiltersFormMobile'
    );

    FacetFiltersForm.renderPage(FacetFiltersForm.createSearchParams(forms), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const { href } = event.currentTarget;
    const query = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
    FacetFiltersForm.renderPage(query, null);
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (!link) return;

    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') ?? document.querySelector('facet-filters-form');
    form?.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
